import { Action, createReducer, on } from '@ngrx/store';
import { IFeatureConfig, IProfileParams } from '@partners/api';
import { ShellActions } from './shell.actions';
import { IShellState } from './shell.state';

export const initialShellState: IShellState = {
  errors: undefined,
  navigation: undefined,
  profile: undefined,
  uiState: {
    progressValue: 0,
    loaded: false,
    loading: false,
    isFooterFixed: false,
    isProgressHidden: true
  }
};

const shellReducerFn = createReducer(
  initialShellState,
  on(ShellActions.reset, (state: IShellState, action) => {
    return {
      ...initialShellState
    };
  }),
  on(ShellActions.updateProfileParams, (state: IShellState, action): IShellState => {
    let { params } = action;

    params = {
      ...params,
      saleOfferCode: Number(params.saleOfferCode),
      salePointCode: Number(params.salePointCode),
      brandCode: Number(params.brandCode),
      lobCode: Number(params.lobCode),
      triggerCode: Number(params.triggerCode)
    } as Partial<IProfileParams>;

    return {
      ...state,
      ...{
        profile: { ...state.profile, ...{ params: { ...(params as IProfileParams) } } }
      }
    };
  }),
  on(ShellActions.updateShellView, (state: IShellState, action): IShellState => {
    const { viewState } = action;

    const uiState = { ...state.uiState, ...viewState };

    return {
      ...state,
      ...{ uiState }
    };
  }),
  on(ShellActions.updateFeatureConfig, (state: IShellState, action): IShellState => {
    const { featureName, featureConfig } = action;

    let { profile } = state;
    let { policy } = profile;
    let { config } = policy;
    let { modules } = config;

    let moduleIndex = modules.findIndex((m) => m.name === featureName);
    let moduleData: IFeatureConfig = modules.find((m) => m.name === featureName);

    if (!moduleData) {
      moduleData = featureConfig;
    }

    moduleData = {
      name: featureName,
      params: { ...moduleData?.params, ...featureConfig?.params },
      links: { ...moduleData?.links, ...featureConfig?.links },
      indication: { ...moduleData?.indication, ...featureConfig?.indication },
      text: { ...moduleData?.text, ...featureConfig?.text }
    };

    let cloned = modules.slice();

    if (moduleIndex < 0) {
      cloned.push(moduleData);
    } else cloned.splice(moduleIndex, 1, moduleData);

    config = { ...config, ...{ modules: [...cloned.slice()] } };
    policy = { ...policy, ...{ config: { ...config } } };
    profile = { ...profile, ...{ policy: { ...policy } } };

    return {
      ...state,
      ...{ profile: { ...profile } }
    };
  }),
  on(ShellActions.updateProgressValue, (state: IShellState, action): IShellState => {
    const { value } = action;

    const uiState = { ...state.uiState, progressValue: value };

    return {
      ...state,
      ...{ uiState }
    };
  }),
  on(ShellActions.updateStages, (state: IShellState, action): IShellState => {
    const { stages } = action;

    const navigationState = { ...state.navigation, stages: [...stages] };

    return {
      ...state,
      ...{ navigation: { ...navigationState } }
    };
  }),
  on(ShellActions.createAnonymousStage, (state: IShellState, action): IShellState => {
    const targetStages = action.stages;

    return {
      ...state,
      navigation: { ...state.navigation, ...{ anonymousStages: [...targetStages] } }
    };
  }),
  on(ShellActions.updateAnonymousStage, (state: IShellState, action): IShellState => {
    const targetStage = action.stage;

    const hasStage = state.navigation.anonymousStages.find(({ url }) => url === targetStage.url);

    let navigation = { ...state.navigation };

    if (!hasStage) {
      navigation = { ...navigation, ...{ anonymousStages: [...navigation.anonymousStages, targetStage] } };
    }

    return {
      ...state,
      navigation: { ...navigation }
    };
  }),
  on(ShellActions.updateFlatStages, (state: IShellState, action): IShellState => {
    const { flatStages } = action;

    const totalsState = flatStages.reduce(
      (acc, stage) => {
        if (stage.isComplete) {
          acc.completeCount++;
        }
        if (stage.isPristine) {
          acc.pristineCount++;
        }
        return acc;
      },
      { completeCount: 0, pristineCount: 0 }
    );

    const navigationState = {
      ...state.navigation,
      flatStages: [...flatStages],
      totalComplete: totalsState.completeCount,
      totalPristine: totalsState.pristineCount
    };
    return {
      ...state,
      ...{ navigation: { ...navigationState } }
    };
  }),
  on(ShellActions.updateRouteNavigation, (state: IShellState, action): IShellState => {
    const { previousRoute, currentRoute } = action;

    const navigationState = {
      ...state.navigation,
      ...{
        previousRoute: { ...previousRoute },
        currentRoute: { ...currentRoute }
      }
    };
    return {
      ...state,
      ...{ navigation: { ...navigationState } }
    };
  }),
  on(ShellActions.updateStageNavigation, (state: IShellState, action): IShellState => {
    const { previousStage, currentStage, nextStage } = action;

    const navigationState = {
      ...state.navigation,
      ...{
        previousStage: { ...previousStage },
        currentStage: { ...currentStage },
        nextStage: { ...nextStage }
      }
    };

    return {
      ...state,
      ...{ navigation: { ...navigationState } }
    };
  }),
  on(ShellActions.updateProcessProfile, (state: IShellState, action) => {
    const { profile } = action;
    let currentProfile = { ...state.profile, processId: profile.processId };

    profile?.params && Object.assign(currentProfile, { ...{ params: { ...profile.params } } });
    profile?.partner && Object.assign(currentProfile, { ...{ partner: { ...profile.partner } } });
    profile?.policy && Object.assign(currentProfile, { ...{ policy: { ...profile.policy } } });

    return {
      ...state,
      ...{
        profile: { ...currentProfile }
      }
    };
  }),
  on(ShellActions.saveAppError, (state: IShellState, action) => {
    const { error } = action;
    const { token } = error;

    const errorsState = { ...state.errors };

    errorsState[token] = error;

    return {
      ...state,
      ...{ errors: { ...errorsState } }
    };
  }),
  on(ShellActions.saveClientProcessId, (state: IShellState, action) => {
    const { clientProcessId: clientProcessId } = action;

    return {
      ...state,
      ...{
        navigation: { ...state.navigation, clientProcessId: clientProcessId }
      }
    };
  } )
);

export function shellReducer(state: IShellState | undefined, action: Action) {
  return shellReducerFn(state, action);
}
